import React, { Fragment, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Dialog, Transition } from '@headlessui/react'
import { ShareIcon, UserAddIcon } from '@heroicons/react/outline'
import { XIcon } from '@heroicons/react/solid'
import { useFormik } from 'formik'
import { initialValues, validationSchema } from '../../forms/InviteCoworkerForm'
import CardInfo from '../CardInfo/CardInfo'
import InputText from '../InputText/InputText'
import Button from '../Button/Button'
import { tagHotjarRecording } from '../../helpers/util'
import { logErrorOnServer } from '../../helpers/serverLogging'
import useAuth from '../../hooks/useAuth'
import useStatus from '../../hooks/useStatus'
import { Message } from 'element-react'
import useAlert from '../../hooks/useAlert'
import useGlobalLoading from '../../hooks/useGlobalLoading'
import copyToClipboard from 'clipboard-copy'

const logError = logErrorOnServer('Invite User')

const InviteCoworkerModal = ({
    buttonVariant,
    buttonText,
    onCancel,
    onUserAdded,
    showMobile = false,
}) => {
    let [isOpen, setIsOpen] = useState(false)
    const { setError, setLoading, setSuccess } = useStatus()
    const { handleChangeAlert } = useAlert()

    const closeModal = () => {
        setIsOpen(false)
    }

    const openModal = () => {
        setIsOpen(true)
    }

    useEffect(() => {
        if (isOpen) {
            tagHotjarRecording('Invite User')
        }
    }, [isOpen])

    const { addNewUser, user, impersonatedBy } = useAuth()
    const companyName = user?.company?.name
    const sapId = user?.company?.sap_id
    const { changeLoading, isLoading } = useGlobalLoading()

    const baseUrl = window.location.href.split('/users')[0]
    const companyNameForUrl = encodeURIComponent(companyName ?? '')
    const urlForCompanySignUp = `${baseUrl}/signup?company=${companyNameForUrl}`

    const onUserInvitedSuccess = response => {
        setSuccess()
        closeModal()
        handleChangeAlert({
            title: 'Invite sent',
            message: 'Your invite user form has been sent.',
            type: 'success',
        })
        changeLoading(false)
        onUserAdded(response?.data?.user)
    }

    const showCopyLinkSuccess = () => {
        Message({
            message: 'Link Copied to Clipboard',
            type: 'success',
            showClose: true,
            duration: 2000,
            customClass: 'el-message--success',
            iconClass: 'el-message__icon el-icon-circle-check',
        })
    }

    const onUserInvitedError = error => {
        tagHotjarRecording('API Error')
        const {
            errors,
            message = 'Please check your internet connection and try again.',
        } = error?.response?.data ?? {}
        const errorMessage =
            errors && Array.isArray(errors.email) && errors.email[0].length > 0
                ? errors.email[0]
                : errors &&
                  Array.isArray(errors.firstName) &&
                  errors.firstName[0].length > 0
                ? errors.firstName[0]
                : errors &&
                  Array.isArray(errors.lastName) &&
                  errors.lastName[0].length > 0
                ? errors.lastName[0]
                : message

        setError()
        handleChangeAlert({
            title: null,
            message: errorMessage,
            type: 'error',
        })
        logError(errorMessage)
        changeLoading(false)
    }

    const {
        getFieldProps,
        handleSubmit,
        isValid,
        isSubmitting,
        errors: inviteErrors,
        touched,
        setSubmitting,
    } = useFormik({
        initialValues: {
            ...initialValues,
            company: companyName,
        },
        validationSchema,
        onSubmit: formValues => {
            setSubmitting(true)
            setLoading()

            // This route (ajax/register) is built into Laravel and triggers this function:
            // App/Http/Controllers/Auth/RegsiterController->register(). (95% sure on that)
            changeLoading(true)
            addNewUser(
                {
                    sapId,
                    ...formValues,
                },
                onUserInvitedSuccess,
                onUserInvitedError,
            )
            setSubmitting(false)
        },
    })

    const renderContent = () => {
        return (
            <>
                {!showMobile && (
                    <h1 className="text-black70 font-semibold text-2xl m-auto">
                        Invite Coworker
                    </h1>
                )}
                <div className="flex w-full justify-between md:flex-row flex-col items-center md:py-6 md:pt-6 pt-4 md:pb-6 pb-4 border-b border-solid border-grayLines gap-4 ">
                    <p className="text-textDarkerGray text-lg font-semibold">
                        Share Company Sign-Up Link
                    </p>
                    <Button
                        onClick={() => {
                            copyToClipboard(urlForCompanySignUp).then(() =>
                                showCopyLinkSuccess(),
                            )
                        }}
                        className="md:w-auto w-full"
                        variant="tertiary"
                        icon={
                            <ShareIcon className="text-textDarkGray w-6 h-6 mr-2" />
                        }
                        text="Copy Link"
                    />
                </div>
                <p className="text-textDarkerGray font-semibold md:my-6 my-4 text-lg">
                    Or fill out this form below
                </p>

                <form
                    onSubmit={handleSubmit}
                    className="flex flex-col items-center justify-center w-full"
                >
                    <div className="w-full md:p-6 p-4 md:pb-6 pb-0 h-auto flex flex-col items-center justify-center rounded-3xl border border-grayLines bg-gray-50">
                        <InputText
                            {...getFieldProps('company')}
                            placeholder="Company Name"
                            label="Company"
                            disabled
                        />
                        <InputText
                            {...getFieldProps('email')}
                            error={touched['email'] && inviteErrors['email']}
                            placeholder="name@example.com"
                            additionalClass="2"
                            label="Email"
                        />
                        <InputText
                            {...getFieldProps('firstName')}
                            error={
                                touched['firstName'] &&
                                inviteErrors['firstName']
                            }
                            placeholder="Please add First Name"
                            additionalClass="mt-2"
                            label="First Name"
                        />
                        <InputText
                            {...getFieldProps('lastName')}
                            error={
                                touched['lastName'] && inviteErrors['lastName']
                            }
                            placeholder="Please add Last Name"
                            additionalClass="mt-2"
                            label="Last Name"
                        />
                    </div>
                    <div className="flex justify-center items-center mt-4 w-full">
                        <Button
                            className="w-full mr-3"
                            text="Cancel"
                            onClick={() => {
                                showMobile ? onCancel() : closeModal()
                            }}
                            variant="tertiary"
                            type="button"
                        />
                        <Button
                            className="w-full ml-3"
                            text="Send Invite"
                            type="submit"
                            variant="secondary"
                            disabled={
                                isSubmitting ||
                                !isValid ||
                                isLoading ||
                                impersonatedBy === 'salesperson'
                            }
                        />
                    </div>
                </form>
            </>
        )
    }

    return (
        <>
            {showMobile ? (
                <div className="flex flex-col items-center justify-between px-4 py-6 w-full">
                    <CardInfo
                        title="Invite Coworker"
                        content={renderContent()}
                        headerActions={[
                            <XIcon
                                key="closeButton"
                                role="button"
                                className="w-6 text-black70"
                                onClick={() => onCancel()}
                            />,
                        ]}
                    />
                </div>
            ) : (
                <>
                    <div className="flex items-center justify-center">
                        <Button
                            variant={buttonVariant}
                            onClick={openModal}
                            text={buttonText}
                            className="md:w-auto w-full"
                            icon={
                                <UserAddIcon className="text-yellow stroke-2 w-6 h-6 mr-2" />
                            }
                        />
                    </div>

                    <Transition appear show={isOpen} as={Fragment}>
                        <Dialog
                            as="div"
                            className="relative z-170"
                            onClose={closeModal}
                        >
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0"
                                enterTo="opacity-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                            >
                                <div className="fixed inset-0 bg-modalBackground bg-opacity-25" />
                            </Transition.Child>

                            <div className="fixed inset-0 overflow-y-auto scrollOnMobile">
                                <div className="flex min-h-full items-center justify-center md:p-4 p-6 text-center">
                                    <Transition.Child
                                        as={Fragment}
                                        enter="ease-out duration-300"
                                        enterFrom="opacity-0 scale-95"
                                        enterTo="opacity-100 scale-100"
                                        leave="ease-in duration-200"
                                        leaveFrom="opacity-100 scale-100"
                                        leaveTo="opacity-0 scale-95"
                                    >
                                        <Dialog.Panel className="w-full max-w-148 border border-grayLines rounded-3xl transform overflow-hidden bg-white md:p-12 p-4 text-center transition-all relative flex flex-col md:items-start items-center justify-start">
                                            {renderContent()}
                                        </Dialog.Panel>
                                    </Transition.Child>
                                </div>
                            </div>
                        </Dialog>
                    </Transition>
                </>
            )}
        </>
    )
}

InviteCoworkerModal.propTypes = {
    buttonText: PropTypes.string,
    buttonVariant: PropTypes.string,
    onSendInvite: PropTypes.func,
    onCancel: PropTypes.func,
    onUserAdded: PropTypes.func.isRequired,
    showMobile: PropTypes.bool,
}

export default InviteCoworkerModal
